import { Injectable } from "@angular/core";
import { Subject } from "rxjs";
import {
  PatientPlanService,
  PatientPlanDto,
} from "src/swagger/api.client.generated";
import { PatientPlans, CalendarPlanList } from "./home.model";
import { DateHelper } from "@app/core/helpers/date.helper";
import * as m from "moment";

@Injectable({
  providedIn: "root",
})
export class HomeService {
  private currentPlanSource = new Subject();
  private loadPlansSource = new Subject<void>();
  private loadCalendarPlanListSource = new Subject<CalendarPlanList>();

  onCurrentplanChanged = this.currentPlanSource.asObservable();

  onPlansLoaded = this.loadPlansSource.asObservable();

  onCalendarPlanListLoaded = this.loadCalendarPlanListSource.asObservable();

  private hasCurrentPlanNow: boolean = false;
  private currentPlanId: number = 0;
  private Plans: PatientPlans = new PatientPlans();
  private CalendarPlans: CalendarPlanList = new CalendarPlanList();
  private isServiceLoaded: boolean = false;

  private patientPlans: PatientPlanDto[] = [];
  private favouritePlans: PatientPlanDto[] = [];

  constructor(private patientPlanService: PatientPlanService) {}

  updateCurrentPlan(value: number) {
    this.currentPlanId = value;
    this.hasCurrentPlanNow = value !== 0;
  }

  get getAllPatientPlans() {
    return this.patientPlans;
  }

  get getAllFavouritePlans() {
    return this.favouritePlans;
  }

  get getAllPlans() {
    return this.Plans;
  }

  get getAllCalendarPlans() {
    return this.CalendarPlans;
  }

  get serviceLoaded() {
    return this.isServiceLoaded;
  }

  get hasCurrentPlan(): boolean {
    return this.hasCurrentPlanNow;
  }

  get hasCurrentPlanId(): number {
    return this.currentPlanId;
  }

  get getPreviousPlans() {
    return this.Plans.parentPlans;
  }
  get getFavouritePlans() {
    return this.Plans.favParentPlans;
  }

  // Computed fresh on every access (rather than cached from the last fetch) so that a plan
  // fetched before the app-day boundary correctly stops being "today's plan" once the day
  // rolls over, even if the app session stays open with no refetch in between.
  //
  // Matches on date components via DateHelper.isPlanOnDay against DateHelper.getCurrentAppDay(),
  // the same "today" PlanService.getTodayPlanId()'s fallback and the plan-home calendar's data
  // already use — not a plain instant-vs-midnight-range comparison. A plan's startDate is a day
  // marker written into the UTC components by DateHelper.buildPlanStartDate, so comparing the
  // instant against a local range only holds for favourable UTC offsets: at UTC-5 a plan stamped
  // 04:00Z parses to 23:00 the previous local evening and misses its own day; the reverse also
  // put a plan on the WRONG following day at positive offsets. This method still did exactly
  // that, despite PlanService.getTodayPlanId()'s own doc comment claiming the range comparison
  // was retired by FLIP-218 — it was retired everywhere else, but getCurrentPlan (which
  // getTodayPlanId checks first, before its own correct fallback) never got migrated.
  get getCurrentPlan(): PatientPlanDto | null {
    const appDay = DateHelper.getCurrentAppDay();
    return this.patientPlans.find((d) => DateHelper.isPlanOnDay(d.startDate, appDay)) ?? null;
  }

  init() {
    this.isServiceLoaded = false;
    this.getPlansFromDb();
  }

  /**
   * Get the plans from the database
   * @returns void
   */
  private getPlansFromDb() {
    const self = this;
    const cd = m().format("YYYY-MM-DD HH:mm:ss");
    const pp = new PatientPlans();

    this.patientPlanService
      .getPatientPlans(cd)
      .subscribe((a: PatientPlanDto[]) => {
        this.patientPlans = a;
        self.loadPlansSource.next();
      });
  }
}
